'use client';

/**
 * Platform-admin Phase 6 — consumer side of system announcements.
 *
 * Renders active, in-window, plan-targeted platform announcements as dismissible
 * banners. Source of truth is system_announcements via the self-scoping
 * active_announcements_for_me() RPC (resolves the caller's tenant + plan
 * server-side). Fail-soft: any error → no banner (never blocks the shell).
 * Dismissal is per-announcement in localStorage.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { createClient } from '../lib/supabase/client';

interface AnnouncementItem {
  id: string;
  title: string;
  body: string | null;
  type: string;
}

const DISMISS_KEY = 'statura.dismissed_announcements';

function bannerStyle(type: string): { wrap: string; chip: string; label: string } {
  switch (type) {
    case 'MAINTENANCE':
      return {
        wrap: 'bg-amber-50 border-amber-300 text-amber-900',
        chip: 'bg-amber-700 text-white',
        label: 'Maintenance',
      };
    case 'RELEASE':
      return {
        wrap: 'bg-violet-50 border-violet-300 text-violet-900',
        chip: 'bg-violet-700 text-white',
        label: 'Release',
      };
    default:
      return {
        wrap: 'bg-sky-50 border-sky-300 text-sky-900',
        chip: 'bg-sky-700 text-white',
        label: 'Info',
      };
  }
}

export function SystemAnnouncementBanner() {
  const [items, setItems] = useState<AnnouncementItem[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      setDismissed(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'));
    } catch {
      /* ignore */
    }
    let cancelled = false;
    const supabase = createClient();
    // active_announcements_for_me() is self-scoping (resolves the caller's tenant +
    // plan from auth.uid() server-side); no tenant arg by design.
    supabase.rpc('active_announcements_for_me').then(
      ({ data }) => {
        if (cancelled) return;
        const rows = ((data as { rows?: AnnouncementItem[] } | null)?.rows ??
          []) as AnnouncementItem[];
        setItems(rows);
      },
      () => {
        /* fail-soft: no banner on error */
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = (id: string) => {
    const next = Array.from(new Set([...dismissed, id]));
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  if (!mounted) return null;
  const visible = items.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map((a) => {
        const s = bannerStyle(a.type);
        return (
          <div key={a.id} className={`border-b px-4 py-2 ${s.wrap}`}>
            <div className="mx-auto flex max-w-7xl items-start gap-3">
              <span
                className={`mt-0.5 inline-flex shrink-0 items-center rounded px-2 py-0.5 text-caption font-bold uppercase tracking-wider ${s.chip}`}
              >
                {s.label}
              </span>
              <p className="flex-1 text-body">
                <strong>{a.title}</strong>
                {a.body ? <span className="ml-1">{a.body}</span> : null}
              </p>
              <button
                onClick={() => dismiss(a.id)}
                className="shrink-0 rounded-control p-0.5 opacity-70 hover:opacity-100"
                aria-label="Dismiss announcement"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}
